import { fabric } from "fabric"
import type Sign from "./sign"
import type SignObjects from "./sign_objects"
import Calc from "@/common/util/calculator"
import contourPath from "@/signomatic/util/contour"

export const contourSize = (size: "s" | "m" | "l") => {
  switch (size) {
    case "l":
      return 30
    case "m":
      return 19
    case "s":
      return 13
  }

  return 0
}

class Contour {
  private readonly sign: Sign
  private readonly objects: SignObjects
  private canvas: fabric.StaticCanvas
  private signCanvas: fabric.Canvas
  private scale = 1

  constructor(sign: Sign, objects: SignObjects) {
    this.sign = sign
    this.objects = objects
  }

  get centerX() {
    return this.canvas.getWidth() / 2
  }

  get centerY() {
    return this.canvas.getHeight() / 2
  }

  get size() {
    if (this.sign.state && this.sign.state.contour) {
      return contourSize(this.sign.state.contour)
    }

    return null
  }

  initCanvas(original: fabric.Canvas) {
    this.signCanvas = original

    const canvasEl = document.createElement("canvas")
    this.canvas = new fabric.StaticCanvas(canvasEl, {
      stateful: false,
      centeredScaling: true,
    })

    this.canvas.enableRetinaScaling = false
    this.canvas.renderOnAddRemove = false
    this.canvas.setWidth(1500)
    this.canvas.setHeight(1000)
  }

  createPath() {
    if (!this.canvas || !this.signCanvas || !this.size) {
      return null
    }

    const data = this.getImages()

    if (!data) {
      return null
    }

    const pathData = contourPath(data, this.size, this.scale / this.sign.defaultScale)

    const fabricPath = new fabric.Path(pathData.path)
    const scale = 1 / this.sign.scale / (this.scale / this.sign.defaultScale)
    const width = (fabricPath.width || 0) * scale
    const height = (fabricPath.height || 0) * scale
    const widthMm = Math.round(Calc.pxToMm(width))
    const heightMm = Math.round(Calc.pxToMm(height))

    pathData.scaleX = this.sign.fullData.min_size.w / widthMm
    pathData.scaleY = this.sign.fullData.min_size.h / heightMm

    if (widthMm >= this.sign.fullData.min_size.w) {
      pathData.scaleX = 1
    }

    if (heightMm >= this.sign.fullData.min_size.h) {
      pathData.scaleY = 1
    }

    pathData.forcedWidth = widthMm * pathData.scaleX
    pathData.forcedHeight = heightMm * pathData.scaleY

    return pathData
  }

  private getImages(): ImageData | null {
    if (!this.canvas) {
      return null
    }

    this.canvas.clear()
    const objects = this.objects.cloneFromState(this.sign.state, this.centerX, this.centerY)

    for (const obj of objects) {
      if (obj.state.side !== (this.sign.state.side || 0)) {
        continue
      }

      this.canvas.add(obj)
    }

    this.scale = this.sign.calcScale(this.sign.state, this.canvas.getWidth(), this.canvas.getHeight())
    this.canvas.zoomToPoint(new fabric.Point(this.centerX, this.centerY), this.scale)
    this.canvas.renderAll()

    return this.canvas.getContext().getImageData(0, 0, this.canvas.getWidth(), this.canvas.getHeight())
  }
}

export default Contour
